<template>
  <div>
    <section id="experience">
      <div class="section-header">
        <h2>Professional Experience</h2>
        <a href="https://storage.googleapis.com/invarozhkov_posts/cv/Ivan_Rozhkov_cv.pdf" 
           target="_blank" 
           class="download-cv-btn"
           download="Ivan_Rozhkov_CV.pdf">
          <i class="fas fa-file-pdf"></i>
          <span>Download CV</span>
          <i class="fas fa-download"></i>
        </a>
      </div>
      <ul>
        <li 
          v-for="(job, index) in experience" 
          :key="job.id" 
          :class="{
            'even-entry': index % 2 === 0, 
            'odd-entry': index % 2 !== 0,
            'visible': isVisible[index]
          }"
          ref="experienceItems"
        >
          <h3>{{ job.title }} at 
            <a :href="job.website" target="_blank">{{ job.company }}</a>
          </h3>
          <p class="period">{{ job.period }}</p>
          <div v-html="job.description"></div>
          <h4>Stack:</h4>
          <p class="tools" v-html="formattedTools(job.tools)"></p>
        </li>
      </ul>
    </section>

    <section id="additional-info">
      <div class="info-grid">
        <div class="info-card">
          <h3><i class="fas fa-graduation-cap"></i> Education</h3>
          <p><strong>Specialist Degree in Industrial and Civil Engineering</strong></p>
          <p>Nizhny Novgorod State University of Architecture</p>
          <p class="period">2009 - 2014</p>
        </div>

        <div class="info-card">
          <h3><i class="fas fa-code"></i> Programming Skills</h3>
          <p class="skills">Golang • Python • PostgreSQL • Cassandra • Redis • Kubernetes • Kafka</p>
          <p style="margin-top: 1rem;">
            <a href="https://leetcode.com/u/ivanrozhkov/" target="_blank" class="leetcode-link">
              <i class="fas fa-external-link-alt"></i> LeetCode Profile
            </a>
          </p>
        </div>

        <div class="info-card">
          <h3><i class="fas fa-language"></i> Languages</h3>
          <p class="languages">English • Russian</p>
        </div>

        <div class="info-card">
          <h3><i class="fas fa-heart"></i> Hobbies</h3>
          <p class="hobbies">Soldering • Computer Games • Cycling</p>
        </div>
      </div>
    </section>

    <!-- Floating CV Download Button -->
    <a href="https://storage.googleapis.com/invarozhkov_posts/cv/Ivan_Rozhkov_cv.pdf" 
       target="_blank" 
       class="floating-cv-btn"
       download="Ivan_Rozhkov_CV.pdf"
       title="Download CV as PDF">
      <i class="fas fa-file-pdf"></i>
    </a>
  </div>
</template>

<script>
import experience from '../ResumeData.js';

export default {
  name: 'ProfessionalExperience',
  data() {
    return {
      experience,
      isVisible: Array(experience.length).fill(false)
    };
  },
  mounted() {
    const options = {
      root: null,
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = [...this.$refs.experienceItems].indexOf(entry.target);
          this.isVisible[index] = true;
          observer.unobserve(entry.target);
        }
      });
    }, options);

    this.$refs.experienceItems.forEach(item => {
      observer.observe(item);
    });
  },
  methods: {
    formattedTools(tools) {
      return tools.join('&nbsp;&nbsp; ');
    }
  }
};
</script>

<style scoped>
section {
  max-width: 800px;
  margin: 0 auto 2rem;
  padding: 2rem;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  font-family: 'Montserrat', sans-serif; /* Use Google Fonts */
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: 5px;
}

.even-entry {
  background: #ffffff;
}

.odd-entry {
  background: #f9f9f9;
}

h2 {
  text-align: center;
  margin-bottom: 1.5rem;
}

/* Section header with download button */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-header h2 {
  margin: 0;
  flex: 1;
}

/* Download CV Button */
.download-cv-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #007acc, #005a9e);
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 122, 204, 0.3);
  position: relative;
  overflow: hidden;
}

.download-cv-btn:before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.download-cv-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 122, 204, 0.4);
  color: white;
  text-decoration: none;
}

.download-cv-btn:hover:before {
  left: 100%;
}

.download-cv-btn i {
  font-size: 1.1rem;
}

.download-cv-btn .fa-file-pdf {
  color: #fff;
}

.download-cv-btn .fa-download {
  font-size: 0.9rem;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-3px);
  }
  60% {
    transform: translateY(-2px);
  }
}

/* Mobile responsiveness for header */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    text-align: center;
  }
  
  .section-header h2 {
    width: 100%;
  }
  
  .download-cv-btn {
    margin: 0 auto;
  }
}

h3 {
  margin: 0 0 0.5rem 0;
}

h4 {
  margin: 1rem 0 0.5rem 0;
  font-size: 0.95rem;
  font-weight: 600;
}

li {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s ease-out, transform 1s ease-out;
}

li.visible {
  opacity: 1;
  transform: translateY(0);
}

/* Style for nested lists in descriptions */
::v-deep ul {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

::v-deep li {
  font-family: inherit;
  margin-bottom: 0.5rem;
  padding: 0;
  background: none;
  opacity: 1;
  transform: none;
  transition: none;
}

::v-deep p {
  margin: 0.5rem 0;
  line-height: 1.6;
}

::v-deep a {
  color: #007acc;
  text-decoration: none;
}

::v-deep a:hover {
  text-decoration: underline;
}

/* Period styling */
.period {
  color: #666;
  font-size: 0.95rem;
  margin: 0.25rem 0 0.75rem 0;
}

/* Tools styling */
.tools {
  color: #555;
  font-size: 0.95rem;
}

/* Additional Info Section */
#additional-info {
  max-width: 800px;
  margin: 2rem auto;
  padding: 1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: #fff;
  padding: 1.5rem;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.info-card h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.1rem;
}

.info-card h3 i {
  margin-right: 0.5rem;
  color: #007acc;
}

.info-card p {
  margin: 0.5rem 0;
  line-height: 1.6;
}

.skills, .languages, .hobbies {
  font-size: 1rem;
  color: #555;
}

.leetcode-link {
  color: #007acc;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.leetcode-link:hover {
  color: #005a9e;
  text-decoration: underline;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .info-card {
    padding: 1rem;
  }
}

/* Floating CV Download Button */
.floating-cv-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #007acc, #005a9e);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(0, 122, 204, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  animation: float 3s ease-in-out infinite;
}

.floating-cv-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 30px rgba(0, 122, 204, 0.6);
  color: white;
}

.floating-cv-btn i {
  font-size: 1.5rem;
}

.floating-cv-btn::after {
  content: 'Download CV';
  position: absolute;
  right: 70px;
  background: #333;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  font-size: 0.9rem;
  white-space: nowrap;
  opacity: 0;
  transform: translateX(10px);
  transition: all 0.3s ease;
  pointer-events: none;
}

.floating-cv-btn::before {
  content: '';
  position: absolute;
  right: 60px;
  border: 8px solid transparent;
  border-left-color: #333;
  opacity: 0;
  transform: translateX(10px);
  transition: all 0.3s ease;
}

.floating-cv-btn:hover::after,
.floating-cv-btn:hover::before {
  opacity: 1;
  transform: translateX(0);
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .floating-cv-btn {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
  }
  
  .floating-cv-btn i {
    font-size: 1.2rem;
  }
  
  .floating-cv-btn::after,
  .floating-cv-btn::before {
    display: none;
  }
}

</style>